/**
 * Training Management Page
 * Manage training categories and items
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link as MuiLink,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableHead,
  Button,
  Tab,
  Tabs,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Add as AddIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { MainLayout } from '../../components/layout/MainLayout';
import { LoadingSpinner, EmptyState } from '../../components/common';
import {
  Card,
  CardContent,
  TextField,
  TableCell,
  TableHeaderCell,
  TableRow,
  PrimaryButton,
  SecondaryButton,
} from '../../components/ui';
import { TrainingService } from '../../services/training/TrainingService';
import type { 
  TrainingAudience,
  TrainingCategory,
  TrainingCategoryFormData,
  TrainingItem,
  TrainingItemFormData
} from '../../types/training';

const AUDIENCE_TABS: Array<{ value: TrainingAudience; label: string }> = [
  { value: 'Asset', label: 'Asset Training Resource' },
  { value: 'Ghost', label: 'Ghost Training Resource' },
];

const getAudienceDescription = (audience: TrainingAudience): string =>
  audience === 'Asset'
    ? 'Manage training categories and items shown to Assets in the mobile app'
    : 'Manage training categories and items shown to Ghosts in the mobile app';

const TrainingPage: React.FC = () => {
  const theme = useTheme();
  const [activeAudience, setActiveAudience] = useState<TrainingAudience>('Asset');
  const [categories, setCategories] = useState<TrainingCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  
  // Category dialogs
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<TrainingCategory | null>(null);
  const [categoryDeleteDialogOpen, setCategoryDeleteDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<TrainingCategory | null>(null);
  const [categoryFormData, setCategoryFormData] = useState<TrainingCategoryFormData>({
    name: '',
    audience: 'Asset',
    order: undefined,
  });
  const [categoryFormErrors, setCategoryFormErrors] = useState<Partial<Record<keyof TrainingCategoryFormData, string>>>({});

  // Item dialogs
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<TrainingItem | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const [itemDeleteDialogOpen, setItemDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<TrainingItem | null>(null);
  const [itemFormData, setItemFormData] = useState<TrainingItemFormData>({
    title: '',
    description: '',
    link: '',
    order: undefined,
    categoryId: '',
  });
  const [itemFormErrors, setItemFormErrors] = useState<Partial<Record<keyof TrainingItemFormData, string>>>({});

  useEffect(() => {
    void fetchCategories(activeAudience);
  }, [activeAudience]);

  const fetchCategories = async (audience: TrainingAudience) => {
    try {
      setIsLoading(true);
      const data = await TrainingService.getTrainingCategories(audience);
      setCategories(data);
      setExpandedCategories(new Set());
    } catch (error) {
      console.error('Failed to fetch training categories:', error);
      setCategories([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCategoryToggle = (categoryId: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  // Category handlers
  const handleOpenAddCategoryDialog = () => {
    setEditingCategory(null);
    setCategoryFormData({
      name: '',
      audience: activeAudience,
      order: undefined,
    });
    setCategoryFormErrors({});
    setCategoryDialogOpen(true);
  };

  const handleOpenEditCategoryDialog = (category: TrainingCategory) => {
    setEditingCategory(category);
    setCategoryFormData({
      name: category.name,
      audience: category.audience,
      order: category.order,
    });
    setCategoryFormErrors({});
    setCategoryDialogOpen(true);
  };

  const handleCloseCategoryDialog = () => {
    setCategoryDialogOpen(false);
    setEditingCategory(null);
    setCategoryFormData({
      name: '',
      audience: activeAudience,
      order: undefined,
    });
    setCategoryFormErrors({});
  };

  const validateCategoryForm = (): boolean => {
    const errors: Partial<Record<keyof TrainingCategoryFormData, string>> = {};
    if (!categoryFormData.name.trim()) {
      errors.name = 'Category name is required';
    }
    setCategoryFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveCategory = async () => {
    if (!validateCategoryForm()) {
      return;
    }

    try {
      if (editingCategory) {
        await TrainingService.updateTrainingCategory(editingCategory.id, {
          name: categoryFormData.name,
          order: categoryFormData.order,
        });
      } else {
        await TrainingService.createTrainingCategory(categoryFormData);
      }
      handleCloseCategoryDialog();
      void fetchCategories(activeAudience);
    } catch (error) {
      console.error('Failed to save training category:', error);
    }
  };

  const handleOpenDeleteCategoryDialog = (category: TrainingCategory) => {
    setCategoryToDelete(category);
    setCategoryDeleteDialogOpen(true);
  };

  const handleCloseDeleteCategoryDialog = () => {
    setCategoryDeleteDialogOpen(false);
    setCategoryToDelete(null);
  };

  const handleDeleteCategory = async () => {
    if (!categoryToDelete) return;

    try {
      await TrainingService.deleteTrainingCategory(categoryToDelete.id);
      handleCloseDeleteCategoryDialog();
      void fetchCategories(activeAudience);
    } catch (error) {
      console.error('Failed to delete training category:', error);
    }
  };

  // Item handlers
  const handleOpenAddItemDialog = (categoryId: string) => {
    setEditingItem(null);
    setSelectedCategoryId(categoryId);
    setItemFormData({
      title: '',
      description: '',
      link: '',
      order: undefined,
      categoryId: categoryId,
    });
    setItemFormErrors({});
    setItemDialogOpen(true);
  };

  const handleOpenEditItemDialog = (item: TrainingItem) => {
    setEditingItem(item);
    setSelectedCategoryId(item.categoryId);
    setItemFormData({
      title: item.title,
      description: item.description,
      link: item.link,
      order: item.order,
      categoryId: item.categoryId,
    });
    setItemFormErrors({});
    setItemDialogOpen(true);
  };

  const handleCloseItemDialog = () => {
    setItemDialogOpen(false);
    setEditingItem(null);
    setSelectedCategoryId('');
    setItemFormData({
      title: '',
      description: '',
      link: '',
      order: undefined,
      categoryId: '',
    });
    setItemFormErrors({});
  };

  const validateItemForm = (): boolean => {
    const errors: Partial<Record<keyof TrainingItemFormData, string>> = {};

    if (!itemFormData.title.trim()) {
      errors.title = 'Title is required';
    }

    if (!itemFormData.description.trim()) {
      errors.description = 'Description is required';
    }

    if (!itemFormData.link.trim()) {
      errors.link = 'Link is required';
    } else {
      try {
        new URL(itemFormData.link);
      } catch {
        errors.link = 'Please enter a valid URL';
      }
    }

    setItemFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveItem = async () => {
    if (!validateItemForm()) {
      return;
    }

    try {
      if (editingItem) {
        await TrainingService.updateTrainingItem(editingItem.id, itemFormData);
      } else {
        await TrainingService.createTrainingItem(itemFormData);
      }
      handleCloseItemDialog();
      void fetchCategories(activeAudience);
    } catch (error) {
      console.error('Failed to save training item:', error);
    }
  };

  const handleOpenDeleteItemDialog = (item: TrainingItem) => {
    setItemToDelete(item);
    setItemDeleteDialogOpen(true);
  };

  const handleCloseDeleteItemDialog = () => {
    setItemDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  const handleDeleteItem = async () => {
    if (!itemToDelete) return;

    try {
      await TrainingService.deleteTrainingItem(itemToDelete.id);
      handleCloseDeleteItemDialog();
      void fetchCategories(activeAudience);
    } catch (error) {
      console.error('Failed to delete training item:', error);
    }
  };

  const handleToggleItemVisibility = async (item: TrainingItem) => {
    try {
      const isHidden = item.hidden === true;

      if (isHidden) {
        await TrainingService.showTrainingItem(item.id);
      } else {
        await TrainingService.hideTrainingItem(item.id);
      }

      await fetchCategories(activeAudience);
    } catch (error) {
      console.error('Failed to update training item visibility:', error);
    }
  };

  return (
    <MainLayout>
      {/* Page Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: theme.spacing(3),
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: theme.palette.text.primary,
              marginBottom: theme.spacing(1),
            }}
          >
            Training Management
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: theme.palette.text.secondary }}
          >
            {getAudienceDescription(activeAudience)}
          </Typography>
        </Box>
        <PrimaryButton
          startIcon={<AddIcon />}
          onClick={handleOpenAddCategoryDialog}
        >
          Add Category
        </PrimaryButton>
      </Box>

      <Card sx={{ marginBottom: theme.spacing(3) }}>
        <CardContent sx={{ paddingBottom: `${theme.spacing(1)} !important` }}>
          <Tabs
            value={activeAudience}
            onChange={(_, value: TrainingAudience) => setActiveAudience(value)}
            aria-label="training audience tabs"
            sx={{
              minHeight: 48,
              '& .MuiTabs-indicator': {
                backgroundColor: theme.palette.primary.main,
                height: 3,
              },
              '& .MuiTab-root': {
                minHeight: 48,
                textTransform: 'none',
                fontWeight: 600,
              },
            }}
          >
            {AUDIENCE_TABS.map((tab) => (
              <Tab key={tab.value} value={tab.value} label={tab.label} />
            ))}
          </Tabs>
        </CardContent>
      </Card>

      {/* Categories List */}
      {isLoading ? (
        <Box sx={{ padding: theme.spacing(6), display: 'flex', justifyContent: 'center' }}>
          <LoadingSpinner />
        </Box>
      ) : categories.length === 0 ? (
        <Card>
          <CardContent>
            <EmptyState
              title="No Training Categories Found"
              message={`There are no ${activeAudience.toLowerCase()} training categories. Click 'Add Category' to create one.`}
            />
          </CardContent>
        </Card>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
          {categories.map((category) => (
            <Card key={category.id}>
              <Accordion
                expanded={expandedCategories.has(category.id)}
                onChange={() => handleCategoryToggle(category.id)}
                sx={{
                  boxShadow: 'none',
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    backgroundColor: 'transparent',
                    padding: theme.spacing(2, 3),
                    '& .MuiAccordionSummary-content': {
                      margin: 0,
                      alignItems: 'center',
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      width: '100%',
                      paddingRight: theme.spacing(2),
                    }}
                  >
                    <Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.text.primary,
                        }}
                      >
                        {category.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.text.secondary,
                          marginTop: theme.spacing(0.5),
                        }}
                      >
                        {category.categoryId} • {category.items?.length || 0} item(s)
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: theme.spacing(2) }}>
                      <MuiLink
                        component="button"
                        variant="body2"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenEditCategoryDialog(category);
                        }}
                        sx={{
                          color: theme.palette.primary.main,
                          cursor: 'pointer',
                          textDecoration: 'none',
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        Edit
                      </MuiLink>
                      <MuiLink
                        component="button"
                        variant="body2"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenDeleteCategoryDialog(category);
                        }}
                        sx={{
                          color: theme.palette.error.main,
                          cursor: 'pointer',
                          textDecoration: 'none',
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        Delete
                      </MuiLink>
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 0 }}>
                  <Box sx={{ padding: theme.spacing(2, 3), paddingTop: 0 }}>
                    {category.items && category.items.length > 0 ? (
                      <>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginBottom: theme.spacing(2) }}>
                          <SecondaryButton
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => handleOpenAddItemDialog(category.id)}
                          >
                            Add Item
                          </SecondaryButton>
                        </Box>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableHeaderCell>Title</TableHeaderCell>
                              <TableHeaderCell>Description</TableHeaderCell>
                              <TableHeaderCell>Link</TableHeaderCell>
                              <TableHeaderCell>Status</TableHeaderCell>
                              <TableHeaderCell>Actions</TableHeaderCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {category.items.map((item) => (
                              <TableRow key={item.id}>
                                <TableCell>{item.title}</TableCell>
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      display: '-webkit-box',
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: 'vertical',
                                      maxWidth: 300,
                                    }}
                                  >
                                    {item.description}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <MuiLink
                                    href={item.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                      color: theme.palette.primary.main,
                                      textDecoration: 'none',
                                      '&:hover': {
                                        textDecoration: 'underline',
                                      },
                                    }}
                                  >
                                    {item.link}
                                  </MuiLink>
                                </TableCell>
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 500,
                                      color: item.hidden === true
                                        ? theme.palette.error.main
                                        : theme.palette.success.main,
                                    }}
                                  >
                                    {item.hidden === true ? 'Hidden' : 'Visible'}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', gap: theme.spacing(2) }}>
                                    <MuiLink
                                      component="button"
                                      variant="body2"
                                      onClick={() => handleOpenEditItemDialog(item)}
                                      sx={{
                                        color: theme.palette.primary.main,
                                        cursor: 'pointer',
                                        textDecoration: 'none',
                                        '&:hover': {
                                          textDecoration: 'underline',
                                        },
                                      }}
                                    >
                                      Edit
                                    </MuiLink>
                                    <MuiLink
                                      component="button"
                                      variant="body2"
                                      onClick={() => handleToggleItemVisibility(item)}
                                      sx={{
                                        color: item.hidden === true
                                          ? theme.palette.success.main
                                          : theme.palette.warning.main,
                                        cursor: 'pointer',
                                        textDecoration: 'none',
                                        '&:hover': {
                                          textDecoration: 'underline',
                                        },
                                      }}
                                    >
                                      {item.hidden === true ? 'Show' : 'Hide'}
                                    </MuiLink>
                                    <MuiLink
                                      component="button"
                                      variant="body2"
                                      onClick={() => handleOpenDeleteItemDialog(item)}
                                      sx={{
                                        color: theme.palette.error.main,
                                        cursor: 'pointer',
                                        textDecoration: 'none',
                                        '&:hover': {
                                          textDecoration: 'underline',
                                        },
                                      }}
                                    >
                                      Delete
                                    </MuiLink>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </>
                    ) : (
                      <Box sx={{ textAlign: 'center', padding: theme.spacing(4) }}>
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, marginBottom: theme.spacing(2) }}>
                          No items in this category
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => handleOpenAddItemDialog(category.id)}
                          sx={{ textTransform: 'none' }}
                        >
                          Add Item
                        </Button>
                      </Box>
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Card>
          ))}
        </Box>
      )}

      {/* Add/Edit Category Dialog */}
      <Dialog
        open={categoryDialogOpen}
        onClose={handleCloseCategoryDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingCategory ? 'Edit Category' : 'Add Category'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2), paddingTop: theme.spacing(1) }}>
            <TextField
              label="Audience"
              value={categoryFormData.audience}
              disabled
              fullWidth
              helperText="Categories stay within the currently selected training resource tab"
            />
            <TextField
              label="Category Name"
              value={categoryFormData.name}
              onChange={(e) => setCategoryFormData({ ...categoryFormData, name: e.target.value })}
              error={!!categoryFormErrors.name}
              helperText={categoryFormErrors.name}
              fullWidth
              required
            />
            <TextField
              label="Order (Optional)"
              type="number"
              value={categoryFormData.order || ''}
              onChange={(e) => setCategoryFormData({ ...categoryFormData, order: e.target.value ? parseInt(e.target.value, 10) : undefined })}
              fullWidth
              helperText="Display order (lower numbers appear first)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <SecondaryButton onClick={handleCloseCategoryDialog}>
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={handleSaveCategory}>
            {editingCategory ? 'Update' : 'Create'}
          </PrimaryButton>
        </DialogActions>
      </Dialog>

      {/* Delete Category Dialog */}
      <Dialog
        open={categoryDeleteDialogOpen}
        onClose={handleCloseDeleteCategoryDialog}
      >
        <DialogTitle>Delete Category</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the category "{categoryToDelete?.name}"? 
            This will also delete all items in this category. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <SecondaryButton onClick={handleCloseDeleteCategoryDialog}>
            Cancel
          </SecondaryButton>
          <PrimaryButton
            onClick={handleDeleteCategory}
            sx={{
              backgroundColor: theme.palette.error.main,
              '&:hover': {
                backgroundColor: theme.palette.error.dark,
              },
            }}
          >
            Delete
          </PrimaryButton>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Item Dialog */}
      <Dialog
        open={itemDialogOpen}
        onClose={handleCloseItemDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingItem ? 'Edit Training Item' : 'Add Training Item'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2), paddingTop: theme.spacing(1) }}>
            <TextField
              label="Category"
              value={categories.find(c => c.id === selectedCategoryId)?.name || ''}
              disabled
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.01)',
                  '&.Mui-disabled': {
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.01)',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.12)',
                      borderStyle: 'dashed',
                    },
                    '& .MuiInputBase-input': {
                      color: theme.palette.text.secondary,
                      WebkitTextFillColor: theme.palette.text.secondary,
                      cursor: 'default',
                    },
                  },
                },
              }}
            />
            <TextField
              label="Title"
              value={itemFormData.title}
              onChange={(e) => setItemFormData({ ...itemFormData, title: e.target.value })}
              error={!!itemFormErrors.title}
              helperText={itemFormErrors.title}
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={itemFormData.description}
              onChange={(e) => setItemFormData({ ...itemFormData, description: e.target.value })}
              error={!!itemFormErrors.description}
              helperText={itemFormErrors.description}
              fullWidth
              multiline
              rows={4}
              required
            />
            <TextField
              label="Link URL"
              value={itemFormData.link}
              onChange={(e) => setItemFormData({ ...itemFormData, link: e.target.value })}
              error={!!itemFormErrors.link}
              helperText={itemFormErrors.link || 'Enter a valid URL (e.g., https://example.com)'}
              fullWidth
              required
            />
            <TextField
              label="Order (Optional)"
              type="number"
              value={itemFormData.order || ''}
              onChange={(e) => setItemFormData({ ...itemFormData, order: e.target.value ? parseInt(e.target.value, 10) : undefined })}
              fullWidth
              helperText="Display order within category (lower numbers appear first)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <SecondaryButton onClick={handleCloseItemDialog}>
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={handleSaveItem}>
            {editingItem ? 'Update' : 'Create'}
          </PrimaryButton>
        </DialogActions>
      </Dialog>

      {/* Delete Item Dialog */}
      <Dialog
        open={itemDeleteDialogOpen}
        onClose={handleCloseDeleteItemDialog}
      >
        <DialogTitle>Delete Training Item</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the item "{itemToDelete?.title}"? This action cannot be undo.
          </Typography>
        </DialogContent>
        <DialogActions>
          <SecondaryButton onClick={handleCloseDeleteItemDialog}>
            Cancel
          </SecondaryButton>
          <PrimaryButton
            onClick={handleDeleteItem}
            sx={{
              backgroundColor: theme.palette.error.main,
              '&:hover': {
                backgroundColor: theme.palette.error.dark,
              },
            }}
          >
            Delete
          </PrimaryButton>
        </DialogActions>
      </Dialog>
    </MainLayout>
  );
};

export default TrainingPage;
